import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Annotation from '../Annotation';
import type { AppTheme, Annotation as AnnotationType } from '../types';

// 注意：不再自动注册 PDF Worker
// Worker 应该由最终调用方在应用入口处通过 registerPDFWorker() 手动注册
// 这样可以避免打包后函数引用问题，并给用户更多控制权

// 默认主题
const defaultTheme: AppTheme = {
  colors: {
    primary: '#007bff',
    secondary: '#6c757d',
    success: '#008000',
    warning: '#FFA500',
    error: '#FF0000',
    info: '#0000FF',
    background: '#f8f9fa',
    text: '#343a40',
    border: '#dee2e6',
    disabled: '#808080',
    disabledBackground: '#F5F5F5',
    disabledText: '#808080',
  },
  space: {
    sidebar: 'md',
    size: 'md',
    radius: 'md',
    padding: 'md',
    margin: 'md',
    shadow: 'md',
    lineHeight: 'md',
  },
};

// PDF渲染组件属性接口
export interface PdfRenderProps {
  setScale?: (scale: number) => void;
  // PDF数据 - 统一接受已解析的PDFDocument对象
  pdfDocument: any | null;

  // 当前页码（全局页码）
  currentPage: number;

  // PDF文档的起始页码（用于分页PDF）
  pdfStartPage?: number;

  // 缩放比例
  scale: number;

  // 标注数据
  annotations: AnnotationType[];

  // 是否处于标注模式
  isAnnotating: boolean;

  // 是否为幻灯片模式（影响布局显示）
  isSlidesMode?: boolean;

  // 事件回调
  onPageChange?: (pageNumber: number) => void;
  onScaleChange?: (scale: number) => void; // 添加缩放更新回调
  onAnnotationChange?: (annotation: AnnotationType) => void;
  onAnnotationDelete?: (annotationId: string) => void;

  // 样式配置
  styles?: {
    theme?: AppTheme;
  };
}

// 样式组件
const LoadingText = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${props => props.$theme.colors.disabledText};
  font-size: 14px;
`;

const ErrorText = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${props => props.$theme.colors.error};
  font-size: 14px;
  text-align: center;
`;

/**
 * PDF渲染组件
 * 负责PDF文件的具体渲染和标注功能
 *
 * @param props - 组件属性
 * @returns PDF渲染组件
 */
const PdfRender: React.FC<PdfRenderProps> = ({
  pdfDocument,
  currentPage,
  pdfStartPage = 1, // 默认从第1页开始
  scale,
  annotations,
  isAnnotating,
  isSlidesMode = false,
  setScale,
  onScaleChange,
  onAnnotationChange,
  onAnnotationDelete,
  styles,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canvasSize, setCanvasSize] = useState<any>(null);
  const [isInitialRender, setIsInitialRender] = useState(true);

  // 使用主题
  const mergedTheme = styles?.theme || defaultTheme;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRendering = useRef(false);
  const pageCurrentNumber = useRef<number | null>(null);

  /**
   * 计算合适的初始缩放比例
   */
  const calculateAutoScale = useCallback(
    (viewport: any) => {
      if (!containerRef.current || !isInitialRender || !onScaleChange) return null;

      const container = containerRef.current;
      const containerWidth = container.offsetWidth - 40; // 减去padding
      const containerHeight = container.offsetHeight - 40; // 减去padding

      // 计算宽度和高度的缩放比例
      const scaleX = containerWidth / viewport.width;
      const scaleY = containerHeight / viewport.height;

      // 选择较小的缩放比例，确保内容完全显示
      const autoScale = Math.min(scaleX, scaleY, 1); // 不超过100%

      // 限制缩放范围在0.3到1之间
      const finalScale = Math.max(0.3, Math.min(1, autoScale));

      console.log('自动计算缩放比例:', {
        containerWidth,
        containerHeight,
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
        scaleX,
        scaleY,
        autoScale,
        finalScale,
      });

      return finalScale;
    },
    [isInitialRender, onScaleChange]
  );

  /**
   * 渲染PDF页面
   */
  const renderPage = useCallback(async () => {
    if (!pdfDocument || !canvasRef.current) return;

    // 防止重复渲染
    if (pageRendering.current || pageCurrentNumber.current === currentPage) {
      console.log('页面正在渲染中，跳过');
      return;
    }

    try {
      pageCurrentNumber.current = currentPage;
      pageRendering.current = true;

      // 计算PDF文档内的相对页码
      const relativePageNumber = currentPage - pdfStartPage + 1;
      console.log(
        `计算下，渲染页面: PDF起始页：pdfStartPage=${pdfStartPage}, 全局页码：currentPage=${currentPage}, 相对页码=${relativePageNumber}`
      );

      // 验证页码是否在PDF文档范围内
      if (relativePageNumber < 1 || relativePageNumber > pdfDocument.numPages) {
        console.error(
          `页码超出范围: 相对页码=${relativePageNumber}, PDF总页数=${pdfDocument.numPages}`
        );
        throw new Error(
          `页码超出范围: 请求第${currentPage}页，但PDF文档只包含${pdfDocument.numPages}页`
        );
      }

      const page = await pdfDocument.getPage(relativePageNumber);
      const canvas = canvasRef.current;

      const context = canvas.getContext('2d');

      // 使用scale直接调整viewport，而不是CSS transform
      console.log('当前scale值:', scale);
      const viewport = page.getViewport({ scale: 1 }); // 使用原始尺寸，让CSS transform处理缩放
      console.log('viewport尺寸:', viewport.width, 'x', viewport.height);

      // 暂时禁用自动缩放，先恢复PDF渲染
      if (isInitialRender) {
        setIsInitialRender(false);
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      if (canvas.width && canvas.height) {
        setCanvasSize({ width: canvas.width, height: canvas.height });
      }

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      console.log('PDF渲染成功, canvas尺寸:', canvas.width, 'x', canvas.height);

      // 确保父容器能正确获取canvas尺寸
      if (containerRef.current) {
        const container = containerRef.current;
        console.log('容器尺寸:', container.offsetWidth, 'x', container.offsetHeight);
        console.log('Canvas显示尺寸:', canvas.offsetWidth, 'x', canvas.offsetHeight);
      }
    } catch (error) {
      console.error('PDF渲染失败:', error);
    } finally {
      pageRendering.current = false;
    }
  }, [pdfDocument, scale, pdfStartPage, calculateAutoScale, onScaleChange, currentPage]);

  // 监听当前页面变化，重新渲染
  useEffect(() => {
    if (pdfDocument && currentPage && pdfStartPage) {
      console.log(
        '计算下，renderPage:',
        'pdfStartPage:' + pdfStartPage,
        'currentPage:' + currentPage,
        'pdfDocument:',
        pdfDocument
      );
      renderPage();
    }
  }, [currentPage, pdfDocument, pdfStartPage]);

  // 监听缩放变化，重新渲染当前页面
  useEffect(() => {
    if (pdfDocument && currentPage) {
      renderPage();
    }
  }, [scale, pdfDocument, currentPage]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <LoadingText $theme={mergedTheme}>加载中...</LoadingText>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <ErrorText $theme={mergedTheme}>
          <div>
            <div>加载失败</div>
            <div style={{ fontSize: '12px', marginTop: '8px' }}>{error}</div>
          </div>
        </ErrorText>
      </div>
    );
  }

  const zoomTimer: any = useRef(null);
  const getInitialZoom = () => {
    const boxWidth = containerRef.current ? containerRef.current.offsetWidth - 40 : null;
    if (!boxWidth) return;
    if (!canvasSize?.width) return;
    const naturalWidth = canvasSize.width;
    const autoScale = boxWidth / naturalWidth;
    const finalScale = +autoScale.toFixed(2);
    if (typeof finalScale === 'number') {
      setScale && setScale(Math.min(finalScale, 1));
    } else {
      clearInterval(zoomTimer.current);
      zoomTimer.current = null;
    }
  };

  useEffect(() => {
    if (!containerRef.current || !canvasSize?.width || !canvasSize?.height) {
      clearInterval(zoomTimer.current);
      zoomTimer.current = null;
      zoomTimer.current = setInterval(() => {
        getInitialZoom();
      }, 34);
      return;
    } else {
      getInitialZoom();
    }
    return () => {
      clearInterval(zoomTimer.current);
      zoomTimer.current = null;
    };
  }, [canvasSize]);

  // 优化布局：支持大尺寸canvas的滚动
  return (
    <div
      ref={containerRef}
      className="bg-gray-100"
      style={{
        display: isSlidesMode ? 'block' : 'flex',
        justifyContent: isSlidesMode ? 'initial' : 'center',
        alignItems: isSlidesMode ? 'initial' : 'flex-start',
        padding: '20px',
        minHeight: '100%',
        width: '100%',
        // 移除overflow设置，让外层容器处理滚动
        overflow: 'visible',
      }}
    >
      {isLoading ? (
        <div className="flex flex-col items-center gap-2 mt-20">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
          <div className="text-xs text-gray-600">PDF加载中...</div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 mt-20 text-red-500">
          <div>渲染错误</div>
          <div className="text-xs">{error}</div>
        </div>
      ) : (
        <div className="flex justify-center items-start">
          <div
            className="relative"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top',
              display: 'inline-block', // 确保容器适应内容尺寸
            }}
          >
            <canvas
              ref={canvasRef}
              className="shadow-lg bg-white border border-gray-300"
              style={{
                display: 'block',
              }}
            />
            <Annotation
              pageNumber={currentPage}
              scale={scale || 1}
              isAnnotating={isAnnotating}
              annotations={annotations}
              onAnnotationAdd={onAnnotationChange}
              onAnnotationDelete={onAnnotationDelete}
              readOnly={!isAnnotating}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfRender;
